import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { SharedService } from 'src/app/modules/gasprecio/services/shared.service';

@Component({
  selector: 'app-select-type-national',
  templateUrl: './select-type-national.component.html',
  styleUrls: ['./select-type-national.component.scss']
})
export class SelectTypeNationalComponent implements OnInit, OnDestroy {

  @Input() rdc: boolean;
  @Input() combu: boolean;

  public items: any[] = [
    { name: 'Nacional', value: '1' },
    { name: 'Por Estado', value: '0' }
  ];
  public nationalSelected: any = '1'; // Initialize with default value '1' (Nacional) instead of empty array
  public ngxDisabled = true;
  nationalSelected$: Subscription;
  page = 'station';
  page$: Subscription;

  constructor(
    public sanitizer: DomSanitizer,
    private sharedService: SharedService
  ) {
    this.nationalSelected$ = sharedService.sharedNational.subscribe((national: any) => {
      // Use strict comparison to check if value actually changed
      // Handle both array and single value cases
      const currentValue = Array.isArray(this.nationalSelected) && this.nationalSelected.length > 0 
        ? this.nationalSelected[0] 
        : (this.nationalSelected === '' ? null : this.nationalSelected);
      
      // Ignore empty string from service - keep default value '1' (Nacional) unless explicitly changed
      // Only update if we receive a valid non-empty value
      if (national !== '' && 
          national !== null && 
          national !== undefined && 
          national !== currentValue) {
        // Normalize the value - ensure it's not an array unless ngx-select requires it
        this.nationalSelected = national;
        
        switch (this.nationalSelected) {
          case '1':
            this.sharedService.nextEntity = null;
            this.sharedService.nextTown = null;
            this.sharedService.nextZone = null;
            break;
          case '0':
            this.sharedService.nextZone = null;
            this.sharedService.nextTown = null;
            break;
          case 0:
            this.sharedService.nextEntity = null;
            this.sharedService.nextTown = null;
            break;
          default:
            break;
        }
      } else if (national === '') {
        // Service emitted empty string - ignore it and keep current value (or default '1')
        // This prevents empty string from overwriting the default selection
        if (this.nationalSelected === '' || 
            this.nationalSelected === null || 
            this.nationalSelected === undefined ||
            (Array.isArray(this.nationalSelected) && this.nationalSelected.length === 0)) {
          // Only set default if we don't have a valid value
          if (!this.rdc && !this.combu) {
            this.nationalSelected = '1';
            // Don't emit back to service to avoid circular updates
          }
        }
      }
    });

    this.page$ = sharedService.sharedPageMetric.subscribe(page => {
      if (page !== this.page) {
        this.page = page;
        if ((this.page === 'evolution' || this.page === 'actual') && this.rdc) {
          this.items = [
            { name: 'Nacional', value: '1' },
            { name: 'Por Zona', value: 0 },
            { name: 'Por Region', value: 1 },
            { name: 'Por Estado', value: '0' },
          ];
        } else if (this.combu) {
          this.items = [
            { name: 'Nacional', value: '1' },
            { name: 'Por Zona', value: 0 },
            { name: 'Por Estado', value: '0' },
          ];
        } else if (this.page !== 'evolution' && this.rdc) {
          this.items = [
            { name: 'Nacional', value: '1' },
            { name: 'Por Zona', value: 0 },
            { name: 'Por Estado', value: '0' },
          ];
        } else {
          this.items = [
            { name: 'Nacional', value: '1' },
            { name: 'Por Estado', value: '0' },
          ];
        }
      }
    });
  }

  public style(data: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(data);
  }

  ngOnInit(): void {
    if (this.rdc || this.combu) {
      this.items = [
        { name: 'Nacional', value: '1' },
        { name: 'Por Zona', value: 0 },
        { name: 'Por Estado', value: '0' },
      ];
    }
    
    // Try to get current value from BehaviorSubject for initial state
    // This ensures the default value is shown if it was set before component initialization
    try {
      const currentValue = (this.sharedService as any).national?.getValue();
      
      // If service has a value, use it (even if it's empty string, let subscription handle it)
      // Otherwise, keep the default '1' we set in the property initialization
      if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
        this.nationalSelected = currentValue;
      }
    } catch (e) {
      // If we can't access the BehaviorSubject, that's okay
      // The subscription will handle it
    }
  }

  ngOnDestroy() {
    this.nationalSelected$.unsubscribe();
    this.page$.unsubscribe();
  }

  changeNational(event: any) {
    // ngx-select may return the value directly or in an array
    // Extract the actual value
    let value = event;
    if (Array.isArray(event) && event.length > 0) {
      value = event[0];
    }
    // If value is an object with a value property, extract it
    if (value && typeof value === 'object' && value.value !== undefined) {
      value = value.value;
    }
    
    this.nationalSelected = value;
    this.sharedService.nextNational = this.nationalSelected;
  }

  isSelected(option: any): boolean {
    // Handle empty array or null/undefined
    // Note: 0 (number) is falsy but valid, so we need to check explicitly
    // If nationalSelected is empty string '', treat it as if it should be '1' (default Nacional)
    let effectiveValue = this.nationalSelected;
    if (effectiveValue === '' || 
        effectiveValue === null || 
        effectiveValue === undefined ||
        (Array.isArray(effectiveValue) && effectiveValue.length === 0)) {
      // If empty and not in rdc/combu mode, default to '1' (Nacional)
      if (!this.rdc && !this.combu) {
        effectiveValue = '1';
      } else {
        return false;
      }
    }
    
    // Handle both single value and array from ngx-select
    let selectedValue: any;
    if (Array.isArray(effectiveValue)) {
      // If array is empty, no selection
      if (effectiveValue.length === 0) {
        return false;
      }
      selectedValue = effectiveValue[0];
      // If the first element is an object with value property, extract it
      if (selectedValue && typeof selectedValue === 'object' && selectedValue.value !== undefined) {
        selectedValue = selectedValue.value;
      }
    } else {
      selectedValue = effectiveValue;
      // Handle nested value property (in case selectedValue is an object)
      if (selectedValue && typeof selectedValue === 'object' && selectedValue.value !== undefined) {
        selectedValue = selectedValue.value;
      }
    }
    
    // Strict comparison (===) to ensure type and value match exactly
    // This is important because this component has mixed types:
    // - '1' (string) for "Nacional"
    // - '0' (string) for "Por Estado"
    // - 0 (number) for "Por Zona"
    // - 1 (number) for "Por Region"
    // We don't want '0' to match 0 or vice versa
    
    // Direct strict comparison (handles all cases including 0 === 0, '0' === '0', etc.)
    return option.value === selectedValue;
  }

}
